import React, { useContext, useRef } from "react";
import { ElementState, Preview } from "@creatomate/preview";
import { Icon } from "@culturehq/components";
import { Trans, errorService, getSubdomain } from "app/utils";
import { useAWSUpload, useSignUpload } from "app/hooks";

import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import { removeSubtitlesElements } from "components/stories/video-studio/sidebar/tabs/subtitle/utils";
import ContextMenuOption from "./ContextMenuOption";

interface ReplaceOptionProps {
  currentElement: ElementState;
  onSetContextMenuVisible: (value: boolean) => void;
  preview: Preview;
}

const ReplaceOption: React.FC<ReplaceOptionProps> = ({
  currentElement,
  onSetContextMenuVisible,
  preview
}) => {
  const { data: { subtitles }, handleUpdateData } = useContext(CreatomateContext);
  const { waitForS3 } = useAWSUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signUpload = useSignUpload();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleUpdateData({ uploadingMedia: true });
    try {
      if (currentElement.source.type === VIDEO_TYPE) {
        const source = preview.getSource();
        const videoElement = source.elements.find((el: any) => el.id === currentElement.source.id);
        if (videoElement) {
          handleUpdateData({
            blockSubtitles: true,
            originalReplacedVideo: videoElement.source
          });
          await removeSubtitlesElements(handleUpdateData, preview, subtitles, true);
        }
      }
      const tempUrl = `https://${getSubdomain()}.culturehq.com/${Date.now()}`;
      await preview.cacheAsset(tempUrl, file);
      await preview.applyModifications({
        [`${currentElement.source.id}.source`]: tempUrl,
        [`${currentElement.source.id}.name`]: `media-${file.name}`
      });
      const mediaUrl = await signUpload(file, () => {});
      await waitForS3(
        2000,
        { [`${currentElement.source.id}.source`]: mediaUrl },
        (modifications: Record<string, any>) => {
          preview.applyModifications(modifications);
          handleUpdateData({ uploadingMedia: false });
        }
      );
    } catch (error) {
      errorService.error(`Error replacing media: ${error}`);
      handleUpdateData({ uploadingMedia: false });
    }
    onSetContextMenuVisible(false);
  };

  const handleReplaceClick = () => {
    // eslint-disable-next-line no-unused-expressions
    fileInputRef.current?.click();
  };

  return (
    <>
      <ContextMenuOption onClick={handleReplaceClick}>
        <Icon className="Replace-Element" icon="refresh" />
        <Trans>Replace</Trans>
      </ContextMenuOption>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept={currentElement.source.type === VIDEO_TYPE ? "video/*" : "image/*"}
        onChange={handleFileChange}
      />
    </>
  );
};

export default ReplaceOption;
